/**
 * MilestoneCommentModal Component
 * Modal for adding/editing milestone comments
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquare, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';

interface MilestoneCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (notes: string) => Promise<void>;
  milestoneTitle: string;
  babyName?: string;
  existingNotes: string;
  isChecked: boolean;
}

export function MilestoneCommentModal({
  isOpen,
  onClose,
  onSave,
  milestoneTitle,
  babyName,
  existingNotes,
  isChecked,
}: MilestoneCommentModalProps) {
  const { t } = useTranslation(['babyDevelopment', 'common']);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNotes(existingNotes);
    }
  }, [isOpen, existingNotes]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(notes);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-teal/20 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-pp-teal" />
              </div>
              <div>
                <h2 className="text-base">{milestoneTitle}</h2>
                {babyName && (
                  <p className="text-white/60 text-sm font-normal">{t('commentModal.for', { name: babyName })}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('commentModal.description')}
            </p>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                {t('commentModal.noteLabel')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('commentModal.notePlaceholder')}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-teal focus:border-transparent resize-none"
                autoFocus
              />
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-teal text-white font-medium hover:bg-pp-teal/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isChecked ? (
                t('commentModal.update')
              ) : (
                t('commentModal.saveAndCheck')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
